import { useState } from 'react';
import {
  Text, Spinner, Badge, Button, tokens,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
} from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { fetchProductMovements } from '../../api/inventory.api';
import { useEntityNames } from '../../hooks/useEntityNames';
import { useLocaleSettings } from '../../hooks/useLocaleSettings';
import { useIntlFormat } from '../../hooks/useIntlFormat';
import { useT } from '../../i18n';
import type { StockItem, WarehouseMovement } from '../../types/api.types';

interface Props {
  item: StockItem | null;
  warehouseId: string;
  onClose: () => void;
}

type DetailTab = 'summary' | 'movements';

const th: React.CSSProperties = { textAlign: 'left', padding: '6px 8px', fontSize: 12, color: tokens.colorNeutralForeground3, borderBottom: `1px solid ${tokens.colorNeutralStroke2}` };
const td: React.CSSProperties = { padding: '6px 8px', fontSize: 13, borderBottom: `1px solid ${tokens.colorNeutralStroke2}` };

/**
 * Panel de detalle por producto: Resumen (stock) y Movimientos (con lote y equipo
 * resueltos a nombre, nunca el GUID).
 */
export function ProductDetailDialog({ item, warehouseId, onClose }: Props) {
  const t = useT();
  const { fmtNum } = useIntlFormat();
  const { formatDate, formatCurrency } = useLocaleSettings();
  const { loteName } = useEntityNames();
  const [tab, setTab] = useState<DetailTab>('summary');

  const productId = item?.productId;
  const { data: movements = [], isLoading, isError } = useQuery<WarehouseMovement[]>({
    queryKey: ['product-movements', warehouseId, productId],
    queryFn: () => fetchProductMovements(warehouseId, productId as string),
    enabled: !!item && !!productId,
  });

  const handleClose = () => { setTab('summary'); onClose(); };
  const last = movements[0];

  return (
    <Dialog open={!!item} onOpenChange={(_, d) => { if (!d.open) handleClose(); }} modalType="modal">
      <DialogSurface style={{ maxWidth: '880px', width: '95vw' }}>
        <DialogBody>
          <DialogTitle>{t.inventory.productDetailTitle}{item ? ` — ${item.productName}` : ''}</DialogTitle>
          <DialogContent>
            <div role="tablist" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {(['summary', 'movements'] as DetailTab[]).map((k) => (
                <Button
                  key={k}
                  role="tab"
                  aria-selected={tab === k}
                  size="small"
                  appearance={tab === k ? 'primary' : 'secondary'}
                  onClick={() => setTab(k)}
                >
                  {k === 'summary' ? t.inventory.productDetailTabSummary : t.inventory.productDetailTabMovements}
                </Button>
              ))}
            </div>

            {tab === 'summary' && item && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                <div>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.inventory.productDetailCurrentStock}</Text>
                  <div><Text weight="semibold" size={500}>{fmtNum(item.quantity)} {item.unit}</Text></div>
                </div>
                <div>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.inventory.tableCategory}</Text>
                  <div><Text>{item.category ?? '—'}</Text></div>
                </div>
                <div>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.inventory.tableCost}</Text>
                  <div><Text>{item.totalCost != null ? formatCurrency(item.totalCost) : '—'}</Text></div>
                </div>
                <div>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.inventory.tableExpiry}</Text>
                  <div><Text>{item.expiryDate ?? '—'}</Text></div>
                </div>
                <div>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.inventory.productDetailMovementCount}</Text>
                  <div><Text>{isLoading ? '…' : movements.length}</Text></div>
                </div>
                <div>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.inventory.productDetailLastMovement}</Text>
                  <div><Text>{last ? formatDate((last.occurredAt ?? '').slice(0, 10)) : '—'}</Text></div>
                </div>
              </div>
            )}

            {tab === 'movements' && (
              isLoading ? (
                <Spinner size="small" label={t.inventory.productDetailLoading} />
              ) : isError ? (
                <Text style={{ color: tokens.colorStatusDangerForeground1 }}>{t.inventory.productDetailError}</Text>
              ) : movements.length === 0 ? (
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.inventory.productDetailNoMovements}</Text>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={th}>{t.inventory.tableDate}</th>
                        <th style={th}>{t.inventory.tableType}</th>
                        <th style={{ ...th, textAlign: 'right' }}>{t.inventory.tableQuantity}</th>
                        <th style={th}>{t.inventory.tableReason}</th>
                        <th style={th}>{t.inventory.tableLote}</th>
                        <th style={th}>{t.inventory.tableEquipment}</th>
                        <th style={th}>{t.inventory.tableRecipient}</th>
                        <th style={th}>{t.inventory.tableUser}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {movements.map((m) => (
                        <tr key={m.movementId}>
                          <td style={td}>{formatDate((m.occurredAt ?? '').slice(0, 10))}</td>
                          <td style={td}>
                            <Badge appearance="tint" color={m.type === 'ENTRY' ? 'success' : 'warning'} size="small">
                              {t.inventory.movementTypes[m.type as keyof typeof t.inventory.movementTypes] ?? m.type}
                            </Badge>
                          </td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmtNum(m.quantity)} {m.unit}</td>
                          <td style={td}>{m.reason || '—'}</td>
                          <td style={td}>{m.loteId ? (loteName(m.loteId, m.loteName) || '—') : '—'}</td>
                          <td style={td}>{m.equipmentName ?? '—'}</td>
                          <td style={td}>{m.recipientName ?? '—'}</td>
                          <td style={td}>{m.recordedByName ?? m.recordedBy}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleClose}>{t.inventory.productDetailClose}</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
